/**
 * Mirrors boxofix/constants/planEntitlements.js for client-side resolution
 * when the API omits effective_entitlements.
 */

export type CreatorPlanClass = 'basic' | 'starter' | 'pro' | 'pro plus';

export type CreatorEntitlements = {
    waived_ugc_fee: boolean;
    instagram_automation: boolean;
    auto_upload: boolean;
    sms_notification: boolean;
    agency: boolean;
};

export type HostEntitlements = {
    auto_upload: boolean;
    waived_platform_fee: boolean;
    instagram_automation: boolean;
    sms_notification: boolean;
    /** Brand Manage / content ops suite */
    content_manager: boolean;
};

export const CREATOR_ENTITLEMENT_KEYS = [
    'waived_ugc_fee',
    'instagram_automation',
    'auto_upload',
    'sms_notification',
    'agency',
] as const;

const CREATOR_ENTITLEMENTS_BY_CLASS: Record<CreatorPlanClass, CreatorEntitlements> = {
    starter: {
        waived_ugc_fee: true,
        instagram_automation: false,
        auto_upload: false,
        sms_notification: false,
        agency: false,
    },
    basic: {
        waived_ugc_fee: true,
        instagram_automation: false,
        auto_upload: false,
        sms_notification: false,
        agency: false,
    },
    pro: {
        waived_ugc_fee: true,
        instagram_automation: true,
        auto_upload: true,
        sms_notification: false,
        agency: false,
    },
    'pro plus': {
        waived_ugc_fee: true,
        instagram_automation: true,
        auto_upload: true,
        sms_notification: true,
        agency: true,
    },
};

const HOST_ENTITLEMENT_KEYS = [
    'auto_upload',
    'waived_platform_fee',
    'instagram_automation',
    'sms_notification',
    'content_manager',
] as const;

const HOST_ENTITLEMENT_DEFAULTS: HostEntitlements = {
    auto_upload: true,
    waived_platform_fee: true,
    instagram_automation: true,
    sms_notification: true,
    content_manager: true,
};

export function normalizeCreatorPlanClass(raw: unknown): CreatorPlanClass | null {
    if (raw == null) return null;
    let v = String(raw).trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
    v = v.replace(/\s+creator$/, '').trim();
    if (v === 'advanced' || v === 'pro+' || v === 'proplus') return 'pro plus';
    if (v in CREATOR_ENTITLEMENTS_BY_CLASS) return v as CreatorPlanClass;
    return null;
}

/** Academy Pro Pass unlocks with Pro or Pro Plus. */
export const PRO_PASS_PLAN_CLASSES: CreatorPlanClass[] = ['pro', 'pro plus'];

export function planMatchesAllowedClasses(
    plan: { name?: string | null; plan_name?: string | null },
    allowed?: readonly CreatorPlanClass[] | null,
): boolean {
    if (!allowed?.length) return true;
    const cls = normalizeCreatorPlanClass(plan.name ?? plan.plan_name);
    return Boolean(cls && allowed.includes(cls));
}

export function resolveCreatorEntitlements(
    planClassOrName?: string | null,
    stored?: Partial<CreatorEntitlements> | null,
    effective?: Partial<CreatorEntitlements> | null,
): CreatorEntitlements {
    const cls = normalizeCreatorPlanClass(planClassOrName);
    const base: CreatorEntitlements = cls
        ? { ...CREATOR_ENTITLEMENTS_BY_CLASS[cls] }
        : {
              waived_ugc_fee: false,
              instagram_automation: false,
              auto_upload: false,
              sms_notification: false,
              agency: false,
          };

    if (stored && typeof stored === 'object') {
        for (const key of CREATOR_ENTITLEMENT_KEYS) {
            if (typeof stored[key] === 'boolean') base[key] = stored[key]!;
        }
    }
    if (effective && typeof effective === 'object') {
        for (const key of CREATOR_ENTITLEMENT_KEYS) {
            if (typeof effective[key] === 'boolean') base[key] = effective[key]!;
        }
    }
    return base;
}

export function resolveHostEntitlements(
    stored?: Partial<HostEntitlements> | null,
    effective?: Partial<HostEntitlements> | null,
): HostEntitlements {
    const base = { ...HOST_ENTITLEMENT_DEFAULTS };
    const apply = (src?: Partial<HostEntitlements> | null) => {
        if (!src || typeof src !== 'object') return;
        for (const key of HOST_ENTITLEMENT_KEYS) {
            if (typeof src[key] === 'boolean') base[key] = src[key]!;
        }
    };
    apply(stored);
    apply(effective);
    return base;
}

export function hasEntitlement(
    entitlements: Partial<CreatorEntitlements | HostEntitlements> | null | undefined,
    key: string,
): boolean {
    return Boolean(entitlements && (entitlements as Record<string, unknown>)[key] === true);
}

export type CreatorEntitlementKey = (typeof CREATOR_ENTITLEMENT_KEYS)[number];

/** Resolve entitlements for a catalog plan row (prefers API effective_entitlements). */
export function resolvePlanEntitlements(plan: {
    name?: string | null;
    plan_name?: string | null;
    entitlements?: Partial<CreatorEntitlements> | null;
    effective_entitlements?: Partial<CreatorEntitlements> | null;
}): CreatorEntitlements {
    return resolveCreatorEntitlements(plan.name ?? plan.plan_name, plan.entitlements, plan.effective_entitlements);
}

/** True when the plan grants at least one of the given entitlement keys. */
export function planGrantsAnyEntitlement(
    plan: {
        name?: string | null;
        plan_name?: string | null;
        entitlements?: Partial<CreatorEntitlements> | null;
        effective_entitlements?: Partial<CreatorEntitlements> | null;
    },
    keys: readonly string[],
): boolean {
    if (!keys.length) return true;
    const entitlements = resolvePlanEntitlements(plan);
    return keys.some(key => hasEntitlement(entitlements, key));
}

/** True when the plan grants every listed entitlement (premium campaign upgrade). */
export function planGrantsAllEntitlements(
    plan: {
        name?: string | null;
        plan_name?: string | null;
        entitlements?: Partial<CreatorEntitlements> | null;
        effective_entitlements?: Partial<CreatorEntitlements> | null;
    },
    keys: readonly string[],
): boolean {
    if (!keys.length) return true;
    const entitlements = resolvePlanEntitlements(plan);
    return keys.every(key => hasEntitlement(entitlements, key));
}

/**
 * Entitlements a creator must unlock for a premium campaign.
 * Prefers still-missing keys; falls back to the campaign's full requirement list.
 */
export function premiumCampaignUnlockEntitlements(
    access?: {
        premium?: boolean;
        missing_entitlements?: readonly string[] | null;
        premium_requirements?: readonly string[] | null;
    } | null,
): CreatorEntitlementKey[] | undefined {
    if (!access?.premium) return undefined;
    const raw = access.missing_entitlements?.length ? access.missing_entitlements : access.premium_requirements;
    if (!raw?.length) return undefined;
    const keys = raw.filter((k): k is CreatorEntitlementKey =>
        (CREATOR_ENTITLEMENT_KEYS as readonly string[]).includes(k),
    );
    return keys.length ? keys : undefined;
}

/**
 * After get-my-plan updates Redux, campaign.premium_access can still be stale until
 * campaign details refetch. Reconcile subscription_required using live entitlements.
 */
export function reconcilePremiumAccessWithLiveEntitlements<
    T extends {
        premium?: boolean;
        can_apply?: boolean;
        blocked_reason_code?: string | null;
        blocked_reason_message?: string | null;
        subscription_required?: boolean;
        missing_entitlements?: readonly string[] | null;
        premium_requirements?: readonly string[] | null;
        missing_connected_platforms?: readonly string[] | null;
    },
>(
    access: T | null | undefined,
    liveEntitlements: Partial<CreatorEntitlements> | null | undefined,
): T | null | undefined {
    if (!access?.premium || access.can_apply) return access;
    if (!liveEntitlements) return access;

    const required = access.premium_requirements?.length
        ? access.premium_requirements
        : access.missing_entitlements ?? [];
    if (!required.length) return access;

    const stillMissing = required.filter(key => !hasEntitlement(liveEntitlements, key));
    if (stillMissing.length > 0) {
        return {
            ...access,
            missing_entitlements: stillMissing as T['missing_entitlements'],
            blocked_reason_code: 'subscription_required',
            blocked_reason_message: 'An active Pro (or higher) subscription is required for this premium campaign.',
            can_apply: false,
            subscription_required: true,
        };
    }

    if (access.blocked_reason_code === 'instagram_automation_scopes') {
        return {
            ...access,
            missing_entitlements: [] as unknown as T['missing_entitlements'],
            subscription_required: false,
        };
    }

    const missingPlatforms = access.missing_connected_platforms ?? [];
    if (missingPlatforms.length > 0) {
        return {
            ...access,
            missing_entitlements: [] as unknown as T['missing_entitlements'],
            subscription_required: false,
            can_apply: false,
            blocked_reason_code: 'connected_accounts_required',
            blocked_reason_message: `Connect your ${missingPlatforms.join(', ')} account${
                missingPlatforms.length > 1 ? 's' : ''
            } to apply to this premium campaign.`,
        };
    }

    return {
        ...access,
        missing_entitlements: [] as unknown as T['missing_entitlements'],
        subscription_required: false,
        can_apply: true,
        blocked_reason_code: null,
        blocked_reason_message: null,
    };
}
